/**
 * PppListCard.tsx
 *
 * Renders the `ppp_list` envelope data emitted by onething__list_ppps.
 */

import { Layers, Inbox } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { PPP_TYPE_BADGE } from "@/components/ppp/ppp-constants";
import { PppLock } from "@/components/ppp/PppLock";
import { PppType } from "@/types";
import { cn } from "@/lib/utils";

/** Data shape emitted by the BE envelope for kind "ppp_list" — a subset of the public PPP DTO. */
export interface PppRow {
  pid: string;
  name: string;
  type?: PppType;
  orderIndex?: number;
  dri?: { fullName: string } | null;
  deliverableCount?: number;
  isConfidential?: boolean;
}

function PppRowItem({ row, rank }: { row: PppRow; rank: number }) {
  return (
    <div className="mx-1 flex items-center gap-2 rounded-lg px-2 py-2">
      <span className="w-5 shrink-0 text-right font-mono text-[10.5px] tabular-nums text-muted-foreground/60">{rank}</span>
      {row.type && (
        <span className={cn("inline-flex shrink-0 items-center rounded border px-1 py-0.5 text-[8.5px] font-bold uppercase tracking-wide", PPP_TYPE_BADGE[row.type])}>
          {row.type}
        </span>
      )}
      <p className="min-w-0 flex-1 truncate text-[13px] font-semibold leading-tight tracking-tight">{row.name}</p>
      {row.isConfidential && <PppLock />}
      <span className="shrink-0 text-[10.5px] text-muted-foreground/70">
        {[row.dri?.fullName, row.deliverableCount !== undefined ? `${row.deliverableCount} deliv.` : null].filter(Boolean).join(" · ")}
      </span>
    </div>
  );
}

interface PppListCardProps {
  summary: string;
  rows: PppRow[];
}

export function PppListCard({ summary, rows }: PppListCardProps) {
  return (
    <Card className="my-2 overflow-hidden rounded-xl border-border/60 shadow-sm dark:shadow-none dark:ring-1 dark:ring-border/40">
      <CardHeader className="flex flex-row items-start gap-2.5 px-4 py-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Layers className="h-3.5 w-3.5" />
        </div>
        <p className="min-w-0 flex-1 text-[13.5px] font-bold leading-tight tracking-tight">{summary}</p>
        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">{rows.length}</span>
      </CardHeader>
      <CardContent className="px-2 pb-2 pt-0">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-muted-foreground">
            <Inbox className="h-6 w-6 opacity-50" />
            <span className="text-xs">No PPPs found.</span>
          </div>
        ) : (
          <div>{rows.map((row, i) => <PppRowItem key={row.pid} row={row} rank={i + 1} />)}</div>
        )}
      </CardContent>
    </Card>
  );
}
